import React from "react";
import { Button, Modal, Pagination, PlusIcon } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { useTranslation } from "react-i18next";

import Wrapper from "~/shared/ui/Wrapper";
import {
  IContestTerritory,
  useGetContestTerritories,
} from "~/entity/ContestTerritories";
import {
  useGetLocationData,
  useGoToPreviousPage,
  useSetLocationData,
} from "~/hooks/locationData";
import { OpenModal } from "~/pages/Editor/type";
import { usePermissions } from "~/shared/context/PermissionContext";
import { usePaginationParams } from "~/shared/hooks/usePaginationParams";
import { TableV2 } from "~/shared/ui/TableV2";

import ContestTerritoriesFilter from "./Filter";
import ContestTerritoryForm from "./modals/ContestTerritoryForm";
import { useContestTerritoriesFilterValues } from "./Filter/useContestTerritoriesFilterValues";
import { getContestTerritoriesColumns } from "./model/tableAdapter";
import { CONTEST_TERRITORIES_OPTIONS } from "./utils/options";

const formId = "ContestTerritoryForm";

const ContestTerritories = () => {
  const { t } = useTranslation();
  const goBack = useGoToPreviousPage();
  const setData = useSetLocationData();
  const { openModal, page, count } = useGetLocationData();
  const { hasPermission } = usePermissions();
  const { date, geoPlaceId } = useContestTerritoriesFilterValues();

  // И в URL, и на бэкенде страница 0-based; +1 только для отображения в Pagination.
  const { data, total, isLoading, refetch } = useGetContestTerritories({
    page,
    count,
    date,
    geoPlaceId,
  });

  const closeModal = () => setData("openModal", "", "currentId", "");
  const openAddModal = () => setData("openModal", OpenModal.Add);

  const handleRowEdit = (record: IContestTerritory) => {
    setData("openModal", OpenModal.Edit, "currentId", record.id);
  };

  const { handleCount, handlePage } = usePaginationParams();

  return (
    <Wrapper.Content
      title={t("contestTerritories.title")}
      goBackHandler={goBack}
      txtBtn={t("ui.back")}
      filterComponent={<ContestTerritoriesFilter />}
      controlComponent={
        <Flex gap={10}>
          {hasPermission("competitiveTerritories.create") && (
            <Button
              onClick={openAddModal}
              size={"middle"}
              icon={<PlusIcon size={16} />}
            >
              {t("ui.add")}
            </Button>
          )}
        </Flex>
      }
    >
      <TableV2<IContestTerritory>
        columns={getContestTerritoriesColumns(t, CONTEST_TERRITORIES_OPTIONS)}
        dataSource={data}
        loading={isLoading}
        rowKey="id"
        {...(hasPermission("competitiveTerritories.edit")
          ? { onRowEdit: handleRowEdit }
          : {})}
      />
      <Pagination
        count={count}
        page={page + 1}
        totalCount={total}
        onChangeCount={handleCount}
        onChangePage={handlePage}
      />

      <Modal
        open={openModal === OpenModal.Add || openModal === OpenModal.Edit}
        title={t(
          openModal === OpenModal.Edit
            ? "contestTerritories.title"
            : "contestTerritories.createTerritory",
        )}
        onCancel={closeModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: formId,
          children: t("ui.save"),
        }}
      >
        <ContestTerritoryForm
          refetch={refetch}
          closeModal={closeModal}
          currentData={data}
          formId={formId}
        />
      </Modal>
    </Wrapper.Content>
  );
};

export default ContestTerritories;
